/**
 * Guards the product page add-to-cart button against double adds.
 *
 * The Spurit free-gift app intercepts the click, adds the product and then the gift.
 * For an "auto" gift it appends an empty `.spurit-ufp-wrapper` to <body>, which the app
 * treats as an open popup and so skips its usual redirect to the cart. Nothing visible
 * happens, the customer clicks again and orders the product twice.
 *
 * This locks the button after the first click, shows the loading state and sends the
 * customer to the cart once the app is done and has rendered nothing visible.
 *
 * Kill switch: append ?atc-guard=off to any URL to disable this script in that browser
 * (persisted via localStorage, so it stays off across pages until re-enabled). This is
 * meant for Spurit to test/verify their own fix without us having to redeploy the theme.
 * ?atc-guard=on re-enables it.
 */
(() => {
  const BUTTON_SELECTOR = "product-form-component .add-to-cart-button";
  const WRAPPER_SELECTOR = ".spurit-ufp-wrapper";
  const CONTENT_GRACE_MS = 1200;
  const UNLOCK_AFTER_MS = 10000;
  const STORAGE_KEY = "atc-guard-disabled";

  try {
    const toggle = new URLSearchParams(window.location.search).get("atc-guard");
    if (toggle === "off") localStorage.setItem(STORAGE_KEY, "1");
    else if (toggle === "on") localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // localStorage unavailable (e.g. private mode) — fall back to always-on.
  }

  let isDisabled = false;
  try {
    isDisabled = localStorage.getItem(STORAGE_KEY) === "1";
  } catch (e) {
    // ignore
  }
  if (isDisabled) return;

  let locked = false;
  let lockedButton = null;
  let unlockTimer;

  const unlock = () => {
    locked = false;
    clearTimeout(unlockTimer);
    lockedButton?.closest("add-to-cart-component")?.removeLoading?.();
    lockedButton = null;
  };

  const goToCart = () => {
    window.location.href = window.FoxTheme?.routes?.cart_url || "/cart";
  };

  // Give the app a moment to render its own toast/popup; only redirect when the wrapper stayed empty.
  const onWrapperAdded = (wrapper) => {
    setTimeout(() => {
      if (!locked) return;
      if (wrapper.isConnected && wrapper.textContent.trim()) return unlock();
      goToCart();
    }, CONTENT_GRACE_MS);
  };

  const observer = new MutationObserver((mutations) => {
    if (!locked) return;
    for (const { addedNodes } of mutations) {
      for (const node of addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const wrapper = node.matches(WRAPPER_SELECTOR) ? node : node.querySelector(WRAPPER_SELECTOR);
        if (wrapper) return onWrapperAdded(wrapper);
      }
    }
  });
  observer.observe(document.body, { childList: true });

  // Capture on window so this runs before the app's own click handlers.
  window.addEventListener(
    "click",
    (event) => {
      const button = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;
      if (!button) return;

      if (locked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      locked = true;
      lockedButton = button;
      button.closest("add-to-cart-component")?.addLoading?.();
      // Safety net so the button never stays locked (e.g. add failed or nothing redirects).
      unlockTimer = setTimeout(unlock, UNLOCK_AFTER_MS);
    },
    true
  );

  document.addEventListener("cart:error", unlock);
})();
